import { TokenService } from "@/services/token.service"
import AccountService from "@/services/account.service"
import ApiService from "@/services/api.service"
import { Mutation } from "@/store/mutations"
import { Action } from "@/store/actions"

const state = {
  authError: null,
  account: null,
  rights: AccountService.defaultRights,
  isAuthenticated: !!TokenService.token(),
  claims: TokenService.getClaims(),
  hasAllAccess: null,
  hasAccessto: null,
  accountSubtype: null,
  refreshTimeout: null
}

const getters = {
  username: (state) => (state.account ? state.account.name : ""),
  entity: (state) => (state.account ? state.account._entity : ""),
  usernameShort: (state) => {
    if (state.account?.name) return state.account.name.substr(0, 2).toLocaleUpperCase()
    return ""
  },
  isAuthenticated: (state) => state.isAuthenticated,
  hasAllAccess: (state) => {
    if (state.claims?.permissions) return state.claims.permissions.includes("all")
    return null
  },
  hasAccessto: (state) => state.claims?.permissions || null,
  accountSubtype: (state) => state.claims?.subtype || null
}

const mutations = {
  [Mutation.auth.error](state, error) { state.authError = error },
  [Mutation.auth.set](state, data) {
    state.authError = null
    state.isAuthenticated = true
    TokenService.tokenSave(data.access_token)
    TokenService.refreshSave(data.refresh_token)
    TokenService.usernameSave(data.logged_in_as)
    TokenService.entitySave(data.entity)
  },
  [Mutation.auth.setAccount](state, data) {
    state.account = data
    state.rights = AccountService.getRights(data.subtype)
  },
  [Mutation.auth.delete](state) {
    state.isAuthenticated = false
    state.error = null
    TokenService.tokenDelete()
    TokenService.refreshDelete()
    TokenService.usernameDelete()
    TokenService.entityDelete()
  },
  [Mutation.auth.setRefreshTimeout](state, timeoutId) {
    state.refreshTimeout = timeoutId
  },
  [Mutation.auth.clearRefreshTimeout](state) {
    if (state.refreshTimeout) {
      clearTimeout(state.refreshTimeout)
      state.refreshTimeout = null
    }
  }
}

const actions = {
  async login(context, credentials) {
    context.commit(Mutation.auth.error, undefined)
    try {
      const login = await ApiService.auth.login(credentials)
      context.commit(Mutation.auth.set, login.data.data)
      context.commit(Mutation.entity, login.data.data.entity)
      ApiService.setHeader()
      const account = await ApiService.auth.getAccount(
        TokenService.username(),
        TokenService.entity()
      )
      if (account.data.data.subtype !== 'sadmin') {
        context.commit(Mutation.auth.delete)
        context.commit(Mutation.auth.error, 'Access restricted to superadmins only.')
        return Promise.reject(new Error('Not a superadmin'))
      }
      context.commit(Mutation.auth.setAccount, account.data.data)
    } catch (error) {
      context.commit(Mutation.auth.delete)
      if ([401, 403].includes(error?.errorInfo?.errorCode)) {
        context.commit(Mutation.auth.error, "Wrong password")
      } else {
        context.commit(Mutation.auth.error, error?.errorInfo?.userMessage || "Login failed")
      }
      return Promise.reject(error)
    }
  },

  logout(context) {
    context.commit(Mutation.auth.delete)
  },

  async check(context) {
    if (!TokenService.token() || !TokenService.entity()) {
      context.commit(Mutation.auth.delete)
      return
    }
    context.commit(Mutation.entity, TokenService.entity())
    ApiService.setHeader()
    if (context.state.account) return
    try {
      const account = await ApiService.auth.getAccount(
        TokenService.username(),
        TokenService.entity()
      )
      context.commit(Mutation.auth.setAccount, account.data.data)
      if (!context.state.entity && TokenService.entity()) {
        context.commit(Mutation.entity, TokenService.entity())
      }
    } catch (error) {
      context.commit(Mutation.auth.error, "Error loading user data")
      context.commit(Mutation.auth.delete)
    }
  },

  async refreshToken(context) {
    try {
      const refreshToken = TokenService.refresh()
      const response = await ApiService.auth.refreshToken(refreshToken)
      const newToken = response.data.data.access_token
      const newRefreshToken = response.data.data.refresh_token
      context.state.refreshAttempts = 0
      TokenService.tokenSave(newToken)
      TokenService.refreshSave(newRefreshToken)
      ApiService.setHeader()
      context.state.claims = TokenService.getClaims()
      return newToken
    } catch (error) {
      context.state.refreshAttempts++
      context.state.lastRefreshError = error
      if (context.state.refreshAttempts >= 3) {
        context.commit(Mutation.auth.delete)
      }
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}